import * as THREE from 'three';
import * as CANNON from 'cannon';
import { PlayerUI } from './PlayerUI.js';

export class Player {
    constructor(assetLoader) {
        this.mesh = new THREE.Group();
        this.assetLoader = assetLoader;
        
        // Movement properties
        this.position = new THREE.Vector3(0, 0, 0);
        this.rotation = new THREE.Euler(0, 0, 0);
        this.rotationSpeed = 3;
        this.currentSpeed = 0;
        this.downhillSpeed = 0; // New property for downhill-specific speed
        this.maxForwardSpeed = 10;
        this.maxBackwardSpeed = -5;
        this.acceleration = 6;
        this.deceleration = 3;
        this.brakeDeceleration = 8;
        
        // Slope influence properties
        this.slopeInfluenceFactor = 0.2; // Strength of slope influence (configurable)
        this.slopeAngleThreshold = 2.0; // Minimum angle (in degrees) before slope influence begins
        this.downhillDecayRate = 3; // How quickly downhill speed decays when not on a downhill slope
        this.maxDownhillBoost = 30; // Maximum additional speed from downhill acceleration
        
        // Physics properties
        this.jumpForce = 2; // Basis-Sprungkraft
        this.isGrounded = false;
        this.modelHeight = 0;
        this.model = null;
        this.rigidBody = null;
        this.shape = null;
        this.physicsWorld = null;
        
        // Debug visualization
        this.showCollisionBody = false;
        this.collisionBodyMesh = null;
        this.wasKeyBPressed = false;
        
        // Debug stats
        this.showDebugStats = false;
        this.wasKeyPPressed = false;
        this.slopeAngle = 0;
        this.contactPoint = new THREE.Vector3();
        this.debugHitPoint = null;
        this.debugNormal = null;
        this.showDebugVisuals = false; // Control from settings
        
        // Wheel
        this.wheel = null;
        this.wheelRadius = 0.5; // Default wheel radius
        
        // Smoothing properties for visual representation
        this.smoothPosition = new THREE.Vector3();
        this.smoothQuaternion = new THREE.Quaternion();
        this.positionLerpFactor = 0.3; // Adjust between 0-1 for smoother/faster transitions
        this.rotationLerpFactor = 0.3; // Adjust between 0-1 for smoother/faster transitions
        this.lastUpdateTime = 0;
        
        // Jump charge properties (Minecraft-style mit dynamischem Balken)
        this.isChargingJump = false;
        this.jumpChargeStart = 0;
        this.jumpChargeAmount = 0;
        this.jumpChargeDirection = 1; // 1 = nach rechts, -1 = nach links
        this.maxJumpCharge = 1.0; // Maximum charge (1.0 = 100%)
        this.jumpCharged = false; // Flag to indicate if a jump has been charged and is ready to execute
        this.spaceKeyWasReleased = true; // Track if space key has been released since last charge
        
        // Sweet Spot ganz rechts
        this.jumpChargeSweetSpotMin = 0.85; // Sweet spot lower bound (85%)
        this.jumpChargeSweetSpotMax = 1.0; // Sweet spot upper bound (100%)
        
        // Endposition in der Mitte
        this.jumpChargeEndZone = 0.5; // Where the charge will end up and stay (middle)
        this.jumpChargeEndZoneRange = 0.1; // Range around end zone
        
        // Sprungkraft für verschiedene Zonen
        this.minJumpForce = 2.0; // Minimum jump force (at 0%)
        this.mediumJumpForce = 4.0; // Medium jump force (at end zone)
        this.sweetSpotJumpForce = 6.0; // Jump force when in sweet spot
        this.calculatedJumpForce = 0; // Store the calculated jump force when charged
        
        // Geschwindigkeit des Ladebalkens
        this.chargeSpeed = 3.0; // Wie schnell der Balken sich bewegt (1.0 = 100% pro Sekunde)
        
        // Create UI instance and initialize UI components
        this.playerUI = new PlayerUI(this);
        this.playerUI.createJumpChargeUI();
        this.playerUI.createDebugStatsUI();
    }

    async loadModel(modelPath) {
        try {
            const model = await this.assetLoader.loadModel(modelPath);
            this.model = model;
            while (this.mesh.children.length > 0) {
                this.mesh.remove(this.mesh.children[0]);
            }
            this.mesh.add(model);
            model.scale.set(0.5, 0.5, 0.5);
            model.rotation.y = Math.PI;
            const box = new THREE.Box3().setFromObject(model);
            const center = box.getCenter(new THREE.Vector3());
            model.position.x -= center.x;
            model.position.z -= center.z;
            this.modelHeight = box.max.y - box.min.y;
            const bottomY = box.min.y;
            model.position.y = -bottomY;
    
            // Find the wheel mesh
            this.wheel = null;
            model.traverse((child) => {
                if (child.name === 'wheel') { // Match the name from Blender
                    this.wheel = child;
                    // Calculate actual wheel radius from the geometry if possible
                    if (child.geometry) {
                        child.geometry.computeBoundingSphere();
                        this.wheelRadius = child.geometry.boundingSphere.radius * 0.5; // Scale matches model scale
                    }
                }
            });
            if (!this.wheel) {
                console.warn('Wheel mesh not found in the model');
            } else {
                console.log('Wheel found, radius:', this.wheelRadius);
            }
    
            model.traverse((child) => {
                if (child.isMesh) {
                    child.castShadow = true;
                    child.receiveShadow = true;
                }
            });
            return true;
        } catch (error) {
            console.error('Error loading player model:', error);
            return false;
        }
    }

    initPhysics(physicsWorld) {
        this.physicsWorld = physicsWorld;
        
        // Check if model exists before proceeding
        if (!this.model) {
            console.error("Cannot initialize physics: Model not loaded");
            return false;
        }
        
        // Get the bounding box of the model
        const box = new THREE.Box3().setFromObject(this.model);
        const size = new THREE.Vector3();
        box.getSize(size);
        
        // Create a narrower box shape that better represents the unicycle
        // Make it narrower in X and Z, but keep Y height
        const narrowerWidth = size.x * 0.5;
        const narrowerDepth = size.z * 0.5;
        
        // Create the physics shape - use a cylinder for better rolling physics
        if (this.wheel) {
            // Use a sphere shape for the wheel contact point
            this.shape = new CANNON.Sphere(this.wheelRadius);
            
            // Create the rigid body
            this.rigidBody = new CANNON.Body({
                mass: 1,
                // Lower the linear damping to allow better movement
                linearDamping: 0.1,
                // Add some angular damping for stability
                angularDamping: 0.5
            });
            
            // Add the shape to the body
            // Offset the shape to be at the bottom of the model
            const shapeOffset = new CANNON.Vec3(0, 0, 0); 
            this.rigidBody.addShape(this.shape, shapeOffset);
            
            // Create collision body visualization (sphere)
            this.createCollisionBodyVisualization(this.shape, shapeOffset);
        } else {
            // Fallback to box if wheel not found
            this.shape = new CANNON.Box(new CANNON.Vec3(narrowerWidth/2, size.y/2, narrowerDepth/2));
            this.rigidBody = new CANNON.Body({
                mass: 1
            });
            this.rigidBody.addShape(this.shape);
            
            // Create collision body visualization (box)
            this.createCollisionBodyVisualization(this.shape);
        }
        
        // Set initial position - adjust Y to make sure it touches the ground
        this.rigidBody.position.set(
            this.mesh.position.x, 
            this.mesh.position.y + this.wheelRadius, // Add wheelRadius to raise the body above ground
            this.mesh.position.z
        );
        
        // Lock rotation except via code
        this.rigidBody.fixedRotation = true;
        
        // Add the body to the world
        this.physicsWorld.addBody(this.rigidBody);
        
        return true;
    }

    // Create a visual representation of the collision body
    createCollisionBodyVisualization(shape, offset = null) {
        // Remove any existing visualization
        if (this.collisionBodyMesh) {
            this.mesh.remove(this.collisionBodyMesh);
        }
        
        let geometry, material;
        
        // Create geometry based on shape type
        if (shape instanceof CANNON.Sphere) {
            geometry = new THREE.SphereGeometry(shape.radius, 16, 16);
            material = new THREE.MeshBasicMaterial({ 
                color: 0x0088ff,
                transparent: true,
                opacity: 0.5,
                wireframe: false
            });
        } else if (shape instanceof CANNON.Box) {
            geometry = new THREE.BoxGeometry(
                shape.halfExtents.x * 2,
                shape.halfExtents.y * 2,
                shape.halfExtents.z * 2
            );
            material = new THREE.MeshBasicMaterial({ 
                color: 0x0088ff,
                transparent: true,
                opacity: 0.5,
                wireframe: false
            });
        } else {
            console.warn('Unsupported shape type for visualization');
            return;
        }
        
        // Create mesh
        this.collisionBodyMesh = new THREE.Mesh(geometry, material);
        this.collisionBodyMesh.visible = this.showCollisionBody;
        
        // Position the collision body at the wheel's position
        if (this.wheel && shape instanceof CANNON.Sphere) {
            // Get the wheel's position in local space
            const wheelPosition = new THREE.Vector3();
            this.wheel.getWorldPosition(wheelPosition);
            this.mesh.worldToLocal(wheelPosition);
            
            // Apply the wheel position to the collision body
            this.collisionBodyMesh.position.copy(wheelPosition);
        } else if (offset) {
            this.collisionBodyMesh.position.copy(offset);
        }
        
        this.mesh.add(this.collisionBodyMesh);
    }

    update(deltaTime, inputManager) {
        // Existing code...
        if (!this.rigidBody || !this.physicsWorld) return;
    
        // Toggle collision body visualization with B key
        if (inputManager.isKeyPressed('KeyB') && !this.wasKeyBPressed) {
            this.showCollisionBody = !this.showCollisionBody;
            if (this.collisionBodyMesh) {
                this.collisionBodyMesh.visible = this.showCollisionBody;
            }
            this.wasKeyBPressed = true;
        }
        
        // Reset key state when released
        if (!inputManager.isKeyPressed('KeyB')) {
            this.wasKeyBPressed = false;
        }
        
        // Toggle debug stats with P key
        if (inputManager.isKeyPressed('KeyP') && !this.wasKeyPPressed) {
            this.showDebugStats = !this.showDebugStats;
            this.wasKeyPPressed = true;
            if (this.showDebugStats && !this.playerUI.debugStatsContainer) {
                this.playerUI.createDebugStatsUI();
            } else if (this.playerUI.debugStatsContainer) {
                this.playerUI.debugStatsContainer.style.display = this.showDebugStats ? 'block' : 'none';
            }
        }
        
        // Reset key state when released
        if (!inputManager.isKeyPressed('KeyP')) {
            this.wasKeyPPressed = false;
        }
    
        // Verbesserte Ground-Check-Logik mit Ray-Casting
        this.isGrounded = this.checkGrounded();
        
        const turnDirection = (inputManager.isKeyPressed('KeyD') ? -1 : 0) + (inputManager.isKeyPressed('KeyA') ? 1 : 0);
        this.rotation.y += turnDirection * this.rotationSpeed * deltaTime;
        const rotationQuaternion = new CANNON.Quaternion();
        rotationQuaternion.setFromAxisAngle(new CANNON.Vec3(0, 1, 0), this.rotation.y);
        this.rigidBody.quaternion.copy(rotationQuaternion);
    
        const accelerating = inputManager.isKeyPressed('KeyW');
        const braking = inputManager.isKeyPressed('KeyS');
        if (accelerating && !braking) {
            this.currentSpeed += this.acceleration * deltaTime;
            this.currentSpeed = Math.min(this.currentSpeed, this.maxForwardSpeed);
        } else if (braking && !accelerating) {
            this.currentSpeed -= this.brakeDeceleration * deltaTime;
            this.currentSpeed = Math.max(this.currentSpeed, this.maxBackwardSpeed);
        } else {
            if (this.currentSpeed > 0) {
                this.currentSpeed -= this.deceleration * deltaTime;
                if (this.currentSpeed < 0) this.currentSpeed = 0;
            } else if (this.currentSpeed < 0) {
                this.currentSpeed += this.deceleration * deltaTime;
                if (this.currentSpeed > 0) this.currentSpeed = 0;
            }
        }
        
        // Apply slope-based acceleration/deceleration
        if (this.isGrounded && Math.abs(this.slopeAngle) > this.slopeAngleThreshold) {
            // Calculate slope influence
            const slopeAcceleration = -this.slopeAngle * this.slopeInfluenceFactor * deltaTime;
            
            if (this.slopeAngle < 0) {
                // Going downhill - add to downhill speed
                this.downhillSpeed -= this.slopeAngle * this.slopeInfluenceFactor * deltaTime;
                
                // Cap the downhill speed boost
                this.downhillSpeed = Math.min(this.downhillSpeed, this.maxDownhillBoost);
                
                // Normal speed is still affected slightly by the slope
                this.currentSpeed += slopeAcceleration * 0.3; // Reduced effect on normal speed
            } else {
                // Going uphill - affects only normal speed
                this.currentSpeed += slopeAcceleration;
                
                // Decay downhill speed faster when going uphill
                this.downhillSpeed -= this.downhillDecayRate * 2 * deltaTime;
            }
            
            // Enforce speed limits on normal speed only
            this.currentSpeed = Math.min(Math.max(this.currentSpeed, this.maxBackwardSpeed), this.maxForwardSpeed);
        } else {
            // On flat ground - normal speed limits apply
            this.currentSpeed = Math.min(Math.max(this.currentSpeed, this.maxBackwardSpeed), this.maxForwardSpeed);
            
            // Gradually decay downhill speed when not on a downhill slope
            this.downhillSpeed -= this.downhillDecayRate * deltaTime;
        }
        
        // Ensure downhill speed doesn't go negative
        this.downhillSpeed = Math.max(0, this.downhillSpeed);
        
        // Display actual speed in debug stats (add this to playerUI)
        this.actualSpeed = this.currentSpeed + this.downhillSpeed;
        
        const forward = new THREE.Vector3(0, 0, 1);
        forward.applyEuler(this.rotation);
        forward.normalize();
        
        // Apply both normal and downhill speed to velocity
        const totalSpeed = this.currentSpeed + this.downhillSpeed;
        const velocity = new CANNON.Vec3(
            forward.x * totalSpeed,
            this.rigidBody.velocity.y,
            forward.z * totalSpeed
        );
        this.rigidBody.velocity.copy(velocity);
        
        // Dynamischer Sprung mit bewegendem Ladebalken - MODIFIZIERT für Aufladung in der Luft
        
        // Prüfen, ob Space gedrückt wird - Erlaubt Aufladung in der Luft
        if (inputManager.isKeyPressed('Space')) {
            // Mark that space key is held down
            this.spaceKeyWasReleased = false;
            
            // Wenn wir nicht bereits laden, starten wir den Ladevorgang
            if (!this.isChargingJump) {
                this.isChargingJump = true;
                this.jumpChargeStart = performance.now();
                this.jumpChargeAmount = 0;
                this.jumpChargeDirection = 1; // Start nach rechts
            } else {
                // Berechne die Zeit seit Beginn der Ladung
                const currentTime = performance.now();
                const elapsedTime = (currentTime - this.jumpChargeStart) / 1000; // in Sekunden
                
                // Bewege den Ladebalken basierend auf der Richtung und der verstrichenen Zeit
                const movement = elapsedTime * this.chargeSpeed;
                
                // Aktualisiere die Ladung basierend auf der aktuellen Richtung und Bewegung
                if (this.jumpChargeDirection === 1) { // Nach rechts
                    // Bewege nach rechts bis zum Sweet Spot Maximum
                    this.jumpChargeAmount = Math.min(movement, this.jumpChargeSweetSpotMax);
                    
                    // Wenn wir das Maximum erreicht haben, ändere die Richtung
                    if (this.jumpChargeAmount >= this.jumpChargeSweetSpotMax) {
                        this.jumpChargeDirection = -1;
                        this.jumpChargeStart = currentTime; // Reset der Zeit für die Rückwärtsbewegung
                    }
                } else { // Nach links/zur Mitte
                    // Bewege zurück zur Mitte
                    const distanceToCenter = Math.abs(this.jumpChargeEndZone - this.jumpChargeSweetSpotMax);
                    const newPosition = this.jumpChargeSweetSpotMax - (movement * distanceToCenter / 0.5);
                    
                    // Begrenze auf die Endzone
                    this.jumpChargeAmount = Math.max(newPosition, this.jumpChargeEndZone);
                }
            }
        } else if (this.isChargingJump) {
            // Space wurde losgelassen, berechne die Sprungkraft
            this.isChargingJump = false;
            this.spaceKeyWasReleased = true; // Mark that space key has been released
            
            // Berechne die Sprungkraft basierend auf der Ladung
            if (this.jumpChargeAmount >= this.jumpChargeSweetSpotMin && 
                this.jumpChargeAmount <= this.jumpChargeSweetSpotMax) {
                // Perfekter Sweet-Spot-Sprung!
                this.calculatedJumpForce = this.sweetSpotJumpForce;
            } else if (Math.abs(this.jumpChargeAmount - this.jumpChargeEndZone) <= this.jumpChargeEndZoneRange/2) {
                // Mittlerer Sprung in der Endzone
                this.calculatedJumpForce = this.mediumJumpForce;
            } else {
                // Sprungkraft linear interpolieren zwischen min und medium
                const normalizedCharge = this.jumpChargeAmount / this.jumpChargeEndZone;
                this.calculatedJumpForce = this.minJumpForce + (this.mediumJumpForce - this.minJumpForce) * normalizedCharge;
            }
            
            this.jumpCharged = true;
            
            // Führe den Sprung sofort aus, wenn wir am Boden sind und der Benutzer die Leertaste losgelassen hat
            if (this.isGrounded && this.spaceKeyWasReleased) {
                this.rigidBody.velocity.y += this.calculatedJumpForce;
                this.isGrounded = false;
                this.jumpCharged = false; // Reset the jump charged flag
                this.calculatedJumpForce = 0;
            }
        } else {
            // Space is not pressed - mark as released
            this.spaceKeyWasReleased = true;
        }
        
        // Aktualisiere die UI für die Sprungladung und Debug-Statistiken
        this.playerUI.updateJumpChargeUI(); 
        this.playerUI.updateDebugStatsUI();
        
        // Create the target position with the y-offset
        const targetPosition = new THREE.Vector3(
            this.rigidBody.position.x,
            this.rigidBody.position.y - this.wheelRadius, // Adjust to account for the raised physics body
            this.rigidBody.position.z
        );
        
        // Create the target quaternion
        const targetQuaternion = new THREE.Quaternion(
            this.rigidBody.quaternion.x,
            this.rigidBody.quaternion.y,
            this.rigidBody.quaternion.z,
            this.rigidBody.quaternion.w
        );
        
        // Calculate adaptive lerp factor based on speed
        // Higher speed = more smoothing to prevent flickering
        let adaptivePosLerp = this.positionLerpFactor;
        let adaptiveRotLerp = this.rotationLerpFactor;
        
        // Apply more smoothing at higher speeds
        if (Math.abs(this.currentSpeed) > 5) {
            const speedFactor = Math.min(Math.abs(this.currentSpeed) / this.maxForwardSpeed, 1);
            adaptivePosLerp = Math.max(0.1, this.positionLerpFactor - (speedFactor * 0.15));
            adaptiveRotLerp = Math.max(0.1, this.rotationLerpFactor - (speedFactor * 0.15));
        }
        
        // Apply smoothing with lerp
        this.smoothPosition.lerp(targetPosition, adaptivePosLerp);
        this.smoothQuaternion.slerp(targetQuaternion, adaptiveRotLerp);
        
        // Apply the smoothed position and rotation to the mesh
        this.mesh.position.copy(this.smoothPosition);
        this.mesh.quaternion.copy(this.smoothQuaternion);
    
        // Removed previous leaning effect code that used Euler rotations
        // Don't manually set rotation.z as it conflicts with quaternion
    
        // Rotate the wheel based on total speed (including downhill speed)
        if (this.wheel) {
            const totalSpeed = this.currentSpeed + this.downhillSpeed;
            const distance = totalSpeed * deltaTime;
            const rotationAngle = distance / this.wheelRadius;
            this.wheel.rotation.x -= rotationAngle; // Reversed rotation direction
        }
    }

    // Neue Methode zum Überprüfen, ob der Spieler auf dem Boden ist
    checkGrounded() {
        if (!this.rigidBody || !this.physicsWorld) return false;
        
        // Raycasting für präzisere Bodenerkennung
        const start = new CANNON.Vec3(
            this.rigidBody.position.x,
            this.rigidBody.position.y - this.wheelRadius + 0.05, // Start slightly above the bottom
            this.rigidBody.position.z
        );
        
        const end = new CANNON.Vec3(
            this.rigidBody.position.x,
            this.rigidBody.position.y - this.wheelRadius - 0.2, // Check slightly below
            this.rigidBody.position.z
        );
        
        const result = new CANNON.RaycastResult();
        this.physicsWorld.raycastClosest(start, end, {
            skipBackfaces: true,
            collisionFilterMask: -1,
            collisionFilterGroup: -1
        }, result);
        
        if (result.hasHit) {
            const hitY = result.hitPointWorld.y;
            const bottomY = this.rigidBody.position.y - this.wheelRadius;
            
            // Store contact point for visualization and slope calculation
            this.contactPoint.set(
                result.hitPointWorld.x,
                result.hitPointWorld.y,
                result.hitPointWorld.z
            );
            
            // Calculate slope angle for debug and physics
            if (result.hitNormalWorld) {
                // Get the player's forward direction (in world space)
                const forward = new THREE.Vector3(0, 0, 1);
                forward.applyEuler(this.rotation);
                forward.normalize();
                
                // Get the ground normal
                const normal = new THREE.Vector3(
                    result.hitNormalWorld.x,
                    result.hitNormalWorld.y,
                    result.hitNormalWorld.z
                );
                
                // Project the forward vector onto the plane defined by the normal
                const perpToNormal = new THREE.Vector3().copy(forward)
                    .sub(normal.clone().multiplyScalar(forward.dot(normal)))
                    .normalize();
                
                // Calculate angle between this projected direction and horizontal plane
                // This gives us the slope angle in the direction of travel
                const horizontalRef = new THREE.Vector3(perpToNormal.x, 0, perpToNormal.z).normalize();
                this.slopeAngle = Math.atan2(
                    perpToNormal.y,
                    new THREE.Vector3(perpToNormal.x, 0, perpToNormal.z).length()
                ) * (180 / Math.PI); // Convert to degrees
                
                // Adjust sign based on whether we're going uphill or downhill
                const projectedForward = new THREE.Vector3(forward.x, 0, forward.z).normalize();
                const projectedPerp = new THREE.Vector3(perpToNormal.x, 0, perpToNormal.z).normalize();
                if (projectedForward.dot(projectedPerp) < 0) {
                    this.slopeAngle = -this.slopeAngle;
                }
                
                // Debug visualization if enabled
                if (this.showDebugVisuals) {
                    // Visualize the hit point
                    if (!this.debugHitPoint) {
                        const geometry = new THREE.SphereGeometry(0.1, 8, 8);
                        const material = new THREE.MeshBasicMaterial({ color: 0xff0000 });
                        this.debugHitPoint = new THREE.Mesh(geometry, material);
                        this.mesh.parent.add(this.debugHitPoint);
                    }
                    this.debugHitPoint.position.copy(this.contactPoint);
                    
                    // Visualize the normal
                    if (!this.debugNormal) {
                        const geometry = new THREE.BufferGeometry();
                        const vertices = new Float32Array(6); // Two points: start and end
                        geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
                        const material = new THREE.LineBasicMaterial({ color: 0x00ff00 });
                        this.debugNormal = new THREE.Line(geometry, material);
                        this.mesh.parent.add(this.debugNormal);
                    }
                    
                    // Update normal visualization
                    const normalStart = this.contactPoint.clone();
                    const normalEnd = normalStart.clone().add(normal.multiplyScalar(1));
                    const positions = this.debugNormal.geometry.attributes.position.array;
                    positions[0] = normalStart.x;
                    positions[1] = normalStart.y;
                    positions[2] = normalStart.z;
                    positions[3] = normalEnd.x;
                    positions[4] = normalEnd.y;
                    positions[5] = normalEnd.z;
                    this.debugNormal.geometry.attributes.position.needsUpdate = true;
                }
            }
            
            if (bottomY < hitY) {
                // Penetrating - adjust position
                this.rigidBody.position.y = hitY + this.wheelRadius;
                // Only reset velocity if moving downward
                if (this.rigidBody.velocity.y < 0) {
                    this.rigidBody.velocity.y = 0;
                }
            }
            return true;
        }
        
        // No hit, remove debug visuals if they exist
        if (this.showDebugVisuals) {
            if (this.debugHitPoint) {
                this.debugHitPoint.visible = false;
            }
            if (this.debugNormal) {
                this.debugNormal.visible = false;
            }
        }
        
        return false;
    }

    getObject() {
        return this.mesh;
    }

    // Apply settings from the game settings menu
    applySettings(settings) {
        if (settings) {
            this.showDebugVisuals = settings.showDebugVisuals;
            
            // Update slope influence factor if it exists in settings
            if (settings.slopeInfluenceFactor !== undefined) {
                this.slopeInfluenceFactor = settings.slopeInfluenceFactor;
            }
            
            // Update slope angle threshold if it exists in settings
            if (settings.slopeAngleThreshold !== undefined) {
                this.slopeAngleThreshold = settings.slopeAngleThreshold;
            }
            
            // If debug visuals are turned off, remove any existing visuals
            if (!this.showDebugVisuals) {
                if (this.debugHitPoint) {
                    const scene = this.mesh.parent;
                    if (scene && this.debugHitPoint.parent === scene) {
                        scene.remove(this.debugHitPoint);
                    }
                    this.debugHitPoint = null;
                }
                if (this.debugNormal) {
                    const scene = this.mesh.parent;
                    if (scene && this.debugNormal.parent === scene) {
                        scene.remove(this.debugNormal);
                    }
                    this.debugNormal = null;
                }
            }
        }
    }
}